<template>
  <div class="log-management">
    <div class="log-header">
      <h2>日志管理</h2>
      <div class="log-actions">
        <router-link to="/" class="action-link">返回主页</router-link>
        <router-link to="/admin" class="action-link">返回管理面板</router-link>
        <select v-model="logType" @change="handleTypeChange" class="log-type-select">
          <option value="general">API Logs</option>
          <option value="business">Business Logs</option>
          <option value="external-ip">外部 IP 访问</option>
        </select>
        <button class="refresh-btn" @click="fetchLogList" :disabled="isLoading">
          <span class="refresh-icon">↻</span> 刷新
        </button>
      </div>
    </div>

    <!-- 错误提示 -->
    <div v-if="showError" class="error-message">
      {{ errorMessage }}
    </div>

    <div class="log-container">
      <!-- Log List Panel -->
      <div class="log-list-panel" :class="{ 'collapsed': selectedLog }">
        <div class="search-box">
          <input 
            type="text" 
            v-model="searchQuery" 
            placeholder="搜索日志文件..."
            @input="filterLogs"
          >
        </div>
        
        <div class="log-list">
          <div 
            v-for="log in filteredLogs" 
            :key="log"
            class="log-item"
            :class="{ 'active': selectedLog === log }"
            @click="selectLog(log)"
          >
            <span class="log-name">{{ log }}</span>
            <span class="log-date">{{ formatLogDate(log) }}</span>
          </div>
        </div>

        <div class="pagination">
          <button 
            @click="currentPage--" 
            :disabled="currentPage === 1"
          >上一页</button>
          <span>第 {{ currentPage }} 页，共 {{ totalPages }} 页</span>
          <button 
            @click="currentPage++" 
            :disabled="currentPage === totalPages"
          >下一页</button>
        </div>
      </div>

      <!-- Log Content Panel -->
      <div class="log-content-panel" v-if="selectedLog">
        <div class="log-content-header">
          <h3>{{ selectedLog }}</h3>
          <div class="log-content-actions">
            <select v-model="linesToShow" @change="fetchLogContent">
              <option value="100">100行</option>
              <option value="200">200行</option>
              <option value="500">500行</option>
              <option value="1000">1000行</option>
            </select>
            <button @click="closeLogContent">关闭</button>
          </div>
        </div>

        <div class="log-content" ref="logContent">
          <div v-if="isLoading" class="loading-spinner"></div>
          <div v-else class="log-lines">
            <div 
              v-for="(line, index) in logContent" 
              :key="index"
              class="log-line"
            >
              <span class="line-number">{{ totalLines - logContent.length + index + 1 }}</span>
              <span class="line-content">{{ line }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getLogList, getLogContent } from '@/services/apiService';
import { useAuthStore } from '@/stores/userStore';

export default {
  name: 'LogManagement',
  data() {
    return {
      logs: [],
      filteredLogs: [],
      selectedLog: null,
      logContent: [],
      currentPage: 1,
      itemsPerPage: 20,
      searchQuery: '',
      isLoading: false,
      linesToShow: 200,
      totalLines: 0,
      showError: false,
      errorMessage: '',
      authCheckInterval: null,
      logType: 'general'
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.filteredLogs.length / this.itemsPerPage);
    },
    paginatedLogs() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredLogs.slice(start, end);
    }
  },
  methods: {
    checkToken() {
      const authStore = useAuthStore();
      if (!authStore.isAuthenticated) {
        this.showError = true;
        this.errorMessage = '登录已过期，请重新登录';
        // 清除 store 中的认证状态
        authStore.setlogout();
        this.$router.push({
          path: '/login',
          query: {
            from: 'logs',
            redirect: this.$route.fullPath
          }
        });
        return false;
      }
      return true;
    },
    async fetchLogList() {
      if (!this.checkToken()) return;
      
      this.isLoading = true;
      try {
        const authStore = useAuthStore();
        const response = await getLogList(this.setLoading, authStore.token, this.logType);
        this.logs = response;
        this.filterLogs();
      } catch (error) {
        console.error('Failed to fetch log list:', error);
        this.logs = [];
        if (error.status === 401) {
          const authStore = useAuthStore();
          authStore.setlogout();
          this.showError = true;
          this.errorMessage = '登录已过期，请重新登录';
          this.$router.push({
            path: '/login',
            query: {
              from: 'logs',
              redirect: this.$route.fullPath
            }
          });
        } else {
          this.showError = true;
          this.errorMessage = '获取日志列表失败，请重试';
        }
      } finally {
        this.isLoading = false;
      }
    },
    filterLogs() {
      if (!this.searchQuery) {
        this.filteredLogs = this.logs;
      } else {
        this.filteredLogs = this.logs.filter(log => 
          log.toLowerCase().includes(this.searchQuery.toLowerCase())
        );
      }
      this.currentPage = 1;
    },
    async selectLog(log) {
      if (!this.checkToken()) return;
      
      this.selectedLog = log;
      await this.fetchLogContent();
    },
    async fetchLogContent() {
      if (!this.checkToken()) return;
      
      this.isLoading = true;
      try {
        const authStore = useAuthStore();
        const response = await getLogContent(
          this.selectedLog,
          parseInt(this.linesToShow),
          this.setLoading,
          authStore.token,
          this.logType
        );
        this.logContent = response.lines;
        this.totalLines = response.totalLines;
      } catch (error) {
        console.error('Failed to fetch log content:', error);
        this.logContent = [];
        this.totalLines = 0;
        if (error.status === 401) {
          const authStore = useAuthStore();
          authStore.setlogout();
          this.showError = true;
          this.errorMessage = '登录已过期，请重新登录';
          this.$router.push({
            path: '/login',
            query: {
              from: 'logs',
              redirect: this.$route.fullPath
            }
          });
        } else {
          this.showError = true;
          this.errorMessage = '获取日志内容失败，请重试';
        }
      } finally {
        this.isLoading = false;
      }
    },
    closeLogContent() {
      this.selectedLog = null;
      this.logContent = [];
    },
    formatLogDate(logName) {
      const dateMatch = logName.match(/\d{4}-\d{2}-\d{2}/);
      return dateMatch ? dateMatch[0] : '';
    },
    setLoading(value) {
      this.isLoading = value;
    },
    handleTypeChange() {
      this.selectedLog = null;
      this.logContent = [];
      this.searchQuery = '';
      this.fetchLogList();
    }
  },
  mounted() {
    if (!this.checkToken()) {
      return;
    }
    this.fetchLogList();
    
    this.authCheckInterval = setInterval(() => {
      if (!this.checkToken()) {
        clearInterval(this.authCheckInterval);
      }
    }, 5000);
  },
  beforeUnmount() {
    if (this.authCheckInterval) {
      clearInterval(this.authCheckInterval);
    }
  }
};
</script>

<style scoped>
.log-management {
  padding: 24px;
  height: 100vh;
  background-color: var(--primary-bg);
  font-size: 0.875rem;
  line-height: 1.6;
}

.log-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.log-header h2 {
  margin: 0;
  font-size: 1.25rem;
  color: var(--text-primary);
}

.log-actions {
  display: flex;
  gap: 12px;
}

.action-link {
  color: var(--text-primary);
  text-decoration: none;
  padding: 6px 12px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  transition: background-color 0.2s;
  font-size: 0.875rem;
}

.action-link:hover {
  background-color: var(--hover-bg);
}

.refresh-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background-color: var(--accent-color);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 0.875rem;
}

.refresh-btn:hover {
  background-color: var(--text-primary);
}

.refresh-icon {
  font-size: 1.2em;
}

.log-type-select {
  padding: 6px 12px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background-color: var(--primary-bg);
  color: var(--text-primary);
  font-size: 0.875rem;
}

.log-container {
  display: flex;
  gap: 24px;
  height: calc(100vh - 100px);
}

.log-list-panel {
  width: 300px;
  background-color: var(--secondary-bg);
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
}

.log-list-panel.collapsed {
  width: 0;
  padding: 0;
  overflow: hidden;
}

.search-box {
  margin-bottom: 16px;
}

.search-box input {
  width: 100%;
  padding: 6px 12px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background-color: var(--primary-bg);
  color: var(--text-primary);
  font-size: 0.875rem;
}

.log-list {
  flex: 1;
  overflow-y: auto;
}

.log-item {
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.875rem;
}

.log-item:hover {
  background-color: var(--hover-bg);
}

.log-item.active {
  background-color: var(--accent-color);
  color: white;
}

.log-name {
  font-weight: 500;
}

.log-date {
  font-size: 0.75rem;
  opacity: 0.8;
}

.log-content-panel {
  flex: 1;
  background-color: var(--secondary-bg);
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
}

.log-content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.log-content-header h3 {
  margin: 0;
  font-size: 1rem;
}

.log-content-actions {
  display: flex;
  gap: 12px;
}

.log-content-actions select {
  padding: 6px 12px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background-color: var(--primary-bg);
  color: var(--text-primary);
  font-size: 0.875rem;
}

.log-content {
  flex: 1;
  overflow-y: auto;
  background-color: var(--primary-bg);
  border-radius: 4px;
  padding: 16px;
  font-family: monospace;
  position: relative;
  font-size: 0.875rem;
}

.loading-spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 40px;
  border: 4px solid var(--border-color);
  border-top: 4px solid var(--accent-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.log-lines {
  white-space: pre-wrap;
}

.log-line {
  display: flex;
  gap: 16px;
  padding: 2px 0;
}

.line-number {
  color: var(--text-secondary);
  user-select: none;
  min-width: 40px;
  font-size: 0.875rem;
}

.line-content {
  flex: 1;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
  padding: 8px;
  font-size: 0.875rem;
}

.pagination button {
  padding: 6px 12px;
  background-color: var(--accent-color);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 0.875rem;
}

.pagination button:disabled {
  background-color: var(--border-color);
  cursor: not-allowed;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 添加错误提示样式 */
.error-message {
  background-color: #ffebee;
  color: #c62828;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 16px;
  text-align: center;
  font-size: 0.875rem;
  border: 1px solid #ef9a9a;
}
</style>
